// Minnesotaänger Mini App — text input.
// Restored to telegram-tt's MessageInput DOM contract so the baseline
// common/Composer.scss applies: `#message-input-text` > `.custom-scroll.input-scroller`
// > `.input-scroller-content` > `textarea.form-control.allow-selection` with the
// absolute `placeholder-text` overlay (anchored by the relative scroller content).
// telegram-tt rendered a tiptap contentEditable with symbols, attachments, voice
// and scheduled sends; v1 is a plain controlled textarea that auto-grows up to
// 10rem (the baseline scroller clamp) and scrolls internally beyond it. Enter
// sends (Shift+Enter inserts a newline, IME composition is respected); the send
// action itself lives in Composer.

import { memo, useEffect, useRef } from '../../../lib/teact/teact';

import type { ElementRef } from '../../../lib/teact/teact';

import useLastCallback from '../../../hooks/useLastCallback';

type OwnProps = {
  text: string;
  inputRef: ElementRef<HTMLTextAreaElement>;
  onTextChange: (text: string) => void;
  onSend: VoidFunction;
};

function MessageInput({ text, inputRef, onTextChange, onSend }: OwnProps) {
  const scrollerRef = useRef<HTMLDivElement>();

  // Auto-grow: the textarea keeps its full content height and the baseline
  // `.input-scroller` does the clamping (max-height 10rem) and scrolls once
  // content overflows it, toggling its baseline `overflown` scrollbar rules.
  const syncScrollerHeight = useLastCallback(() => {
    const textarea = inputRef.current;
    const scroller = scrollerRef.current;
    if (!textarea || !scroller) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
    scroller.classList.toggle('overflown', scroller.scrollHeight > scroller.clientHeight);
  });

  // Re-measure on every text change (typing, paste, send reset).
  useEffect(() => {
    syncScrollerHeight();
  }, [text, syncScrollerHeight]);

  const handleKeyDown = useLastCallback((
    e: { key: string; shiftKey: boolean; isComposing: boolean; preventDefault: VoidFunction },
  ) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      onSend();
    }
  });

  return (
    <div id="message-input-text">
      <div ref={scrollerRef} className="custom-scroll input-scroller">
        <div className="input-scroller-content" data-stricterdom-ignore>
          <textarea
            ref={inputRef}
            id="editable-message-text"
            className="form-control allow-selection"
            value={text}
            dir="auto"
            rows={1}
            aria-label="发消息…"
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {!text && (
            <span className="placeholder-text" dir="auto">发消息…</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(MessageInput);
